import { type Token, marked } from 'marked';

export const STREAM_FADE_MS = 300;
export const STREAM_FADE_CLASS = 'le-stream-fade';

const UNCOUNTED_TEXT = '.le-answer-table-hint';

const OBSERVER_OWNED_ATTRIBUTES = new Set(['data-overflow', 'style']);

const HTML_VOID_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

function updateHtmlContainers(raw: string, containers: Array<string>): void {
  marked.Lexer.lexInline(raw, { gfm: true }).forEach((token) => {
    if (token.type !== 'html') {
      return;
    }
    const tag = /^<(\/?)([a-z][\w-]*)\b/i.exec(token.raw);
    if (!tag) {
      return;
    }
    const name = tag[2].toLowerCase();
    if (tag[1]) {
      const open = containers.lastIndexOf(name);
      if (open >= 0) {
        containers.splice(open);
      }
    } else if (!HTML_VOID_TAGS.has(name) && !/\/\s*>$/.test(token.raw)) {
      containers.push(name);
    }
  });
}

export function splitMarkdownBlocks(markdown: string): { blocks: Array<string>; definitions: string } {
  const tokens = marked.lexer(markdown, { gfm: true });
  const containers: Array<string> = [];
  const blocks = tokens.reduce<Array<string>>((all, token) => {
    if (token.type === 'def') {
      return all;
    }
    const insideContainer = containers.length > 0;
    if (token.type === 'html') {
      updateHtmlContainers(token.raw, containers);
    }
    if ((insideContainer || token.type === 'space') && all.length > 0) {
      all[all.length - 1] += token.raw;
    } else {
      all.push(token.raw);
    }
    return all;
  }, []);
  const definitions = Object.entries(tokens.links)
    .map(([label, { href, title }]) => `[${label}]: <${href}>${title ? ` "${title.replace(/"/g, '\\"')}"` : ''}`)
    .join('\n');
  return { blocks, definitions };
}

function markerText(tokens: Array<Token>): string {
  return tokens
    .map((token) => {
      if (
        ['code', 'codespan', 'html', 'image', 'escape', 'def'].includes(token.type) ||
        (token.type === 'text' && token.escaped)
      ) {
        return '';
      }
      if (token.type === 'link') {
        return token.raw.startsWith('[') ? markerText(token.tokens ?? []) : '';
      }
      if (token.type === 'strong' || token.type === 'em' || token.type === 'del') {
        const width = token.type === 'em' ? 1 : 2;
        return `${token.raw.slice(0, width)}${markerText(token.tokens ?? [])}${token.raw.slice(-width)}`;
      }
      if (token.type === 'table') {
        return [...token.header, ...token.rows.flat()].map((cell) => markerText(cell.tokens)).join('');
      }
      if (token.type === 'list') {
        return token.items.map((item) => markerText(item.tokens)).join('');
      }
      return 'tokens' in token && token.tokens ? markerText(token.tokens) : token.raw;
    })
    .join('');
}

function closeUnpairedMarker(text: string, marker: string): string {
  const syntax = markerText(marked.lexer(text, { gfm: true }));
  if (syntax.split(marker).length % 2 === 1) {
    return text;
  }
  const trimmed = text.trimEnd();
  return trimmed.endsWith(marker) ? trimmed.slice(0, -marker.length) : `${trimmed}${marker}`;
}

type InlineCodeScan = { outsideCode: string; openRun: { index: number; length: number } | null };

function maskMarkdownEscapes(text: string): string {
  return text.replace(/\\[\\`*{}[\]()#+\-.!_>~|]/g, '  ');
}

function scanInlineCode(text: string): InlineCodeScan {
  const runs = [...text.matchAll(/`+/g)];
  let outsideCode = text;
  for (let open = 0; open < runs.length; open += 1) {
    const escaped = (text.slice(0, runs[open].index).match(/\\+$/)?.[0].length ?? 0) % 2 === 1;
    const start = (runs[open].index ?? 0) + Number(escaped);
    const length = runs[open][0].length - Number(escaped);
    if (length === 0) {
      continue;
    }
    const close = runs.findIndex((run, index) => index > open && run[0].length === length);
    const end = close < 0 ? text.length : (runs[close].index ?? 0) + length;
    outsideCode = `${outsideCode.slice(0, start)}${outsideCode.slice(start, end).replace(/[^\n]/g, ' ')}${outsideCode.slice(end)}`;
    if (close < 0) {
      return { outsideCode, openRun: { index: start, length } };
    }
    open = close;
  }
  return { outsideCode, openRun: null };
}

function closeOpenInlineCode(text: string): string {
  const { openRun } = scanInlineCode(text);
  if (!openRun) {
    return text;
  }
  const code = text.slice(openRun.index + openRun.length);
  return code.trim() ? `${text}${'`'.repeat(openRun.length)}` : text.slice(0, openRun.index);
}

const CONTAINER_PREFIX = /^(\s*(>|[-*+](?=\s)|\d+[.)](?=\s)))*\s*/;

function withinContainer(line: string): string {
  return line.replace(CONTAINER_PREFIX, '');
}

function trailingBlock(tokens: Array<Token>): Token | undefined {
  const tail = [...tokens].reverse().find((token) => token.type !== 'space' && token.type !== 'def');
  if (tail?.type === 'blockquote') {
    return trailingBlock(tail.tokens ?? []);
  }
  if (tail?.type === 'list') {
    return trailingBlock(tail.items[tail.items.length - 1]?.tokens ?? []);
  }
  return tail;
}

function withoutUnfinishedTable(block: string): string {
  const lines = block.split('\n');
  const unfinished = lines.length - 1;
  const normalized = lines.map(withinContainer).join('\n');
  const tail = trailingBlock(marked.lexer(block, { gfm: true }));
  const table = tail?.type === 'table' ? tail.raw : null;
  if (!table) {
    const delimiter = withinContainer(lines[unfinished]);
    const header = unfinished > 0 ? maskMarkdownEscapes(scanInlineCode(lines[unfinished - 1]).outsideCode) : '';
    const pendingDelimiter =
      header.includes('|') && delimiter.includes('|') && delimiter.includes('-') && /^[\s|:-]+$/.test(delimiter);
    return pendingDelimiter ? `${lines.slice(0, unfinished).join('\n')}\n` : block;
  }
  const tableStart = normalized.lastIndexOf(table.split('\n').map(withinContainer).join('\n').trimEnd());
  if (tableStart < 0) {
    return block;
  }
  const header = normalized.slice(0, tableStart).split('\n').length - 1;
  const shownLines = header + 1 >= unfinished ? lines.slice(0, header) : lines.slice(0, unfinished);
  return shownLines.length > 0 ? `${shownLines.join('\n')}\n` : '';
}

function withoutPartialLink(text: string): string {
  const partial = /!?\[([^\]\n]*)\]\([^)\n]*$/.exec(maskMarkdownEscapes(scanInlineCode(text).outsideCode));
  if (!partial) {
    return text;
  }
  const labelStart = partial.index + partial[0].indexOf('[') + 1;
  return `${text.slice(0, partial.index)}${text.slice(labelStart, labelStart + partial[1].length)}`;
}

export function healStreamingTail(block: string): string {
  if (/^\s*(```|~~~)/m.test(block) || trailingBlock(marked.lexer(block, { gfm: true }))?.type === 'code') {
    return block;
  }
  const tableHealed = withoutUnfinishedTable(block);
  if (tableHealed !== block) {
    return tableHealed;
  }
  const healed = closeOpenInlineCode(withoutPartialLink(block));
  return ['**', '~~'].reduce(closeUnpairedMarker, healed);
}

type Reveal = { start: number; at: number };

function countedTextNodes(root: Node): Array<Text> {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      !node.nodeValue?.trim() || node.parentElement?.closest(UNCOUNTED_TEXT)
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT,
  });
  const nodes: Array<Text> = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    nodes.push(node as Text);
  }
  return nodes;
}

function fadeSpan(text: string, reveal: Reveal, now: number): HTMLSpanElement {
  const span = document.createElement('span');
  span.className = STREAM_FADE_CLASS;
  span.dataset.streamAt = String(reveal.at);
  span.style.animationDelay = `${-(now - reveal.at)}ms`;
  span.textContent = text;
  return span;
}

function wrapRecentText(nodes: Array<Text>, fading: Array<Reveal>, now: number): void {
  if (fading.length === 0) {
    return;
  }
  const revealAt = (offset: number) => [...fading].reverse().find((reveal) => reveal.start <= offset) ?? null;
  let offset = 0;
  nodes.forEach((node) => {
    const text = node.nodeValue ?? '';
    const start = offset;
    offset += text.length;
    if (offset <= fading[0].start) {
      return;
    }
    const cuts = [0, ...fading.map((reveal) => reveal.start - start).filter((cut) => cut > 0 && cut < text.length)];
    node.replaceWith(
      ...cuts.map((cut, index) => {
        const piece = text.slice(cut, cuts[index + 1] ?? text.length);
        const reveal = revealAt(start + cut);
        return reveal ? fadeSpan(piece, reveal, now) : document.createTextNode(piece);
      }),
    );
  });
}

function attributeSignature(element: Element): string {
  return [...element.attributes]
    .filter((attribute) => !OBSERVER_OWNED_ATTRIBUTES.has(attribute.name))
    .map((attribute) => `${attribute.name}=${attribute.value}`)
    .sort()
    .join('\n');
}

function sameShape(live: Node, next: Node): boolean {
  if (live.nodeType !== next.nodeType) {
    return false;
  }
  if (!(live instanceof Element) || !(next instanceof Element)) {
    return true;
  }
  return (
    live.tagName === next.tagName &&
    attributeSignature(live) === attributeSignature(next) &&
    (!live.hasAttribute('data-stream-at') || live.textContent === next.textContent)
  );
}

function reconcile(live: Node, next: Node): void {
  const liveChildren = [...live.childNodes];
  const nextChildren = [...next.childNodes];
  nextChildren.forEach((nextChild, index) => {
    const liveChild = liveChildren[index];
    if (!liveChild) {
      live.appendChild(nextChild);
    } else if (!sameShape(liveChild, nextChild)) {
      live.replaceChild(nextChild, liveChild);
    } else if (liveChild.nodeType === Node.TEXT_NODE) {
      if (liveChild.nodeValue !== nextChild.nodeValue) {
        liveChild.nodeValue = nextChild.nodeValue;
      }
    } else if (!(liveChild as Element).hasAttribute('data-stream-at')) {
      reconcile(liveChild, nextChild);
    }
  });
  liveChildren.slice(nextChildren.length).forEach((extra) => extra.parentNode?.removeChild(extra));
}

export class StreamingHtml {
  private reveals: Array<Reveal> = [];

  private shownLength = 0;

  patch(target: HTMLElement, html: string, now: number): boolean {
    const template = document.createElement('template');
    template.innerHTML = html;
    const nodes = countedTextNodes(template.content);
    const length = nodes.reduce((sum, node) => sum + (node.nodeValue?.length ?? 0), 0);
    if (length > this.shownLength) {
      this.reveals.push({ start: this.shownLength, at: now });
    } else if (length < this.shownLength) {
      this.reveals = this.reveals.filter((reveal) => reveal.start < length);
    }
    this.shownLength = length;
    this.reveals = this.reveals.filter((reveal) => now - reveal.at < STREAM_FADE_MS);
    wrapRecentText(nodes, this.reveals, now);
    reconcile(target, template.content);
    return this.reveals.length > 0;
  }
}
